import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  FileText,
  Info,
  Network,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";
import { WizardLayout } from "@/components/layout/wizard-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/ui/PaywallButton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { LoadingServiceIdentification } from "@/components/ui/loading-service-identification";
import { LOADING_DURATIONS } from "@/config/loadingDurations";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useRelatieServiciiHandlers } from "@/hooks/useRelatieServiciiHandlers";
import { supabase } from "@/integrations/supabase/client";
import type {
  RelatieStatRomanData,
  SituatieServiciiUEData,
} from "@/types/entity";
import { haveServicesChanged } from "@/utils/serviceUtils";
import {
  MIN_LOADING_DURATION_MS,
  SECTOARE,
  SUBSECTOARE,
} from "../../constants";
import type { EntityData } from "../../types/entity";
import type { CodCaenAutorizat } from "@/types/identificareFirma";
import { CaenPrincipalSelector } from "@/components/onboarding/shared/CaenPrincipalSelector";
import { useUpdateEntityField } from "@/hooks/api/mutate/useUpdateEntityField";
import type { SelectedService } from "../../types/service";
import { SelectedServicesSummary } from "@/components/onboarding/formular-notificare/SelectedServicesSummary";
import { ServiceChangesWarningModal } from "./ServiceChangesWarningModal";
import { ServiceInfoModal } from "./ServiceInfoModal";
import { ServiceTabs } from "./service-tabs";
import { Step2AdditionalSections } from "./Step2AdditionalSections";

const STEPS = [
  "Identificare",
  "Dimensiune",
  "Servicii NIS 2",
  "Esențiale",
  "Importante",
  "Rezultate",
  "Generare Raport",
];

interface Step2Props {
  entityData: EntityData;
  onNext: (
    selectedServices: SelectedService[],
    sectorData: Record<string, unknown>,
    caenSecundar: string[],
    relatieStatRoman: RelatieStatRomanData,
    situatieServiciiUE: SituatieServiciiUEData
  ) => void;
  onBack: () => void;
  onStepClick?: (stepIndex: number) => void;
  userAvatar?: React.ReactNode;
  currentStep?: number;
  steps?: string[];
}

export interface LookupService {
  name: string;
  note_DNSC: string;
  category_id: string;
  category_name: string;
  sector_id: string;
  sector_name: string;
  subsector_id: string | null;
  subsector_name: string | null;
  entity_id: string;
  CAEN_rev_2: string[];
  CAEN_description_rev_2: string;
  CAEN_rev_2_detailed?: Array<{ caen: string; description: string }>; // Array of objects with caen and description
  CAEN_rev_3: string[];
  CAEN_description_rev_3: string;
  CAEN_rev_3_detailed?: Array<{ caen: string; description: string }>; // Array of objects with caen and description
  relationship_type: string;
  context: string;
  verification_status: string;
  observations: string;
  id: string;
  caen_version: number;
  descriere_juridica: string | null;
  descriere_practica: string | null;
  nota_descriere: string | null;
}

export interface IdentifiedService extends LookupService {
  caen: boolean;
  lista_details: {
    url_name: string;
    url: string;
    descriere: string;
  }[];
  lista_oficiala: boolean;
  scor_incredere: number;
  scor_descriere?: string;
  smart_search: boolean;
  super_ambiguu?: {
    byCaen_principal?: boolean;
    byCaen_secundar?: boolean;
  };
  is_super_ambiguu?: boolean;
}

export function Step2ServiceMapping({
  entityData,
  onNext,
  onBack,
  onStepClick,
  currentStep = 2,
  steps = STEPS,
  userAvatar,
}: Step2Props) {
  const { currentSessionId } = useSessionSwitcher();

  // Section states
  const [manualSearchOpen, setManualSearchOpen] = useState(false);

  // New merged section states
  const [selectedAnexa, setSelectedAnexa] = useState<"1" | "2">("1");
  const [activeService, setActiveService] = useState<IdentifiedService | null>(
    null
  );
  const [hoveredCaen, setHoveredCaen] = useState<string>("");

  // Service info modal state
  const [serviceInfoModalOpen, setServiceInfoModalOpen] = useState(false);
  const [selectedServiceInfo, setSelectedServiceInfo] =
    useState<LookupService | null>(null);
  const [selectedListaDetails, setSelectedListaDetails] = useState<
    IdentifiedService["lista_details"][0] | null
  >(null);
  const [selectedSmartSearch, setSelectedSmartSearch] =
    useState<boolean>(false);

  // Service selections - merged approach
  const [anexa1Selections, setAnexa1Selections] = useState<SelectedService[]>(
    []
  );
  const [anexa2Selections, setAnexa2Selections] = useState<SelectedService[]>(
    []
  );
  const [manualServices, setManualServices] = useState<SelectedService[]>([]);
  console.log("🚀 ~ Step2ServiceMapping ~ manualServices:", manualServices);

  const [caenSecundar, setCaenSecundar] = useState<string[]>([]);
  const [coduriAutorizate, setCoduriAutorizate] = useState<CodCaenAutorizat[]>(
    []
  );
  const [caenPrincipalLipsa, setCaenPrincipalLipsa] = useState(false);
  const [reincarcare, setReincarcare] = useState(0);
  const actualizeazaCamp = useUpdateEntityField();
  const [identifiedServices, setIdentifiedServices] = useState<
    IdentifiedService[]
  >([]);
  const hasCompletedStep2Before =
    (entityData.selected_services?.length ?? 0) > 0;
  const [isLoadingIdentifiedServices, setIsLoadingIdentifiedServices] =
    useState(true);
  const [showInitialLoading, setShowInitialLoading] = useState(
    !hasCompletedStep2Before
  );

  // Dropdown states for manual search
  const [selectedSector, setSelectedSector] = useState<string>("");
  const [selectedSubsector, setSelectedSubsector] = useState<string>("");

  // Relatie Stat Roman & Servicii UE sections
  const {
    relatieStatRoman,
    situatieServiciiUE,
    entityDataForSections,
    sectionErrors,
    handlers,
    validate: validateSections,
  } = useRelatieServiciiHandlers(entityData);
  const [showWarningModal, setShowWarningModal] = useState(false);

  // Constants for clear selection
  const CLEAR_VALUE = "__clear__";
  const [remainingServices, setRemainingServices] = useState<LookupService[]>(
    []
  );

  // Auto-select first service when tab changes
  useEffect(() => {
    const servicesForCurrentTab = identifiedServices.filter(
      (s) => s.category_id === selectedAnexa
    );
    if (
      servicesForCurrentTab.length > 0 &&
      (!activeService || activeService.category_id !== selectedAnexa)
    ) {
      setActiveService(servicesForCurrentTab[0]);
    }
  }, [selectedAnexa, activeService, identifiedServices]);

  const [isLoadingAllServices, setIsLoadingAllServices] = useState(true);
  const [isLoadingCAENLookup, setIsLoadingCAENLookup] = useState(false);

  // Fetch CAEN lookup data first, then fetch services
  useEffect(() => {
    const startTime = Date.now();
    const MIN_LOADING_TIME = LOADING_DURATIONS.caenLookupMin;

    const fetchData = async () => {
      // Step 1: Check special lists
      try {
        const { data: raspuns, error: identifiedServicesError } =
          await supabase.functions.invoke(
            `api/identificare-servicii?sessionId=${encodeURIComponent(currentSessionId)}`,
            {
              method: "GET",
            }
          );

        if (identifiedServicesError) {
          throw identifiedServicesError;
        }

        const identifiedServices: IdentifiedService[] = Array.isArray(
          raspuns?.servicii
        )
          ? raspuns.servicii
          : [];
        setIdentifiedServices(identifiedServices);
        setCoduriAutorizate(raspuns?.coduri_autorizate ?? []);
        setCaenPrincipalLipsa(raspuns?.caen_principal_lipsa === true);
        setIsLoadingIdentifiedServices(false);

        // Auto-select services based on entityData.selectedServices if available, otherwise use identified services
        if (
          entityData.selected_services &&
          entityData.selected_services.length > 0
        ) {
          const anexa1Services = entityData.selected_services.filter(
            (service) => service.category_id === "1"
          );
          const anexa2Services = entityData.selected_services.filter(
            (service) => service.category_id === "2"
          );

          setAnexa1Selections(anexa1Services);
          setAnexa2Selections(anexa2Services);

          // Auto-switch to Anexa 2 if Anexa 1 is empty but Anexa 2 has services
          if (anexa1Services.length === 0 && anexa2Services.length > 0) {
            setSelectedAnexa("2");
          }

          const hasEntityDataListaSpecialaField =
            entityData.selected_services.some(
              (service) =>
                service.lista_oficiala === true ||
                service.lista_oficiala === false
            );

          // fallback for old data type of entityData.selected_services to populate with new fields
          if (!hasEntityDataListaSpecialaField) {
            const newSelectedServices = entityData.selected_services.map(
              (service) => {
                const foundIdentifiedService = identifiedServices.find(
                  (s) => s.entity_id === service.entity_id
                );

                return {
                  ...service,
                  lista_oficiala:
                    foundIdentifiedService?.lista_oficiala || false,
                  lista_details: foundIdentifiedService?.lista_details || [],
                  smart_search: foundIdentifiedService?.smart_search || false,
                };
              }
            );
            setManualServices(newSelectedServices);
          } else {
            setManualServices(entityData.selected_services);
          }
        } else if (identifiedServices.length > 0) {
          const anexa1Services = identifiedServices
            .filter((service) => service.category_id === "1")
            .map((service) => ({
              id: service.id,
              entity_id: service.entity_id,
              category_id: service.category_id,
              sector_name: service.sector_name,
              subsector_name: service.subsector_name || "",
              name: service.name,
            }));

          const anexa2Services = identifiedServices
            .filter((service) => service.category_id === "2")
            .map((service) => ({
              id: service.id,
              entity_id: service.entity_id,
              category_id: service.category_id,
              sector_name: service.sector_name,
              subsector_name: service.subsector_name || "",
              name: service.name,
            }));

          setAnexa1Selections(anexa1Services);
          setAnexa2Selections(anexa2Services);

          // Auto-switch to Anexa 2 if Anexa 1 is empty but Anexa 2 has services
          if (anexa1Services.length === 0 && anexa2Services.length > 0) {
            setSelectedAnexa("2");
          }

          // Also select in manualServices with caen_codes
          const allServices = identifiedServices.map((service) => ({
            id: service.id,
            entity_id: service.entity_id,
            category_id: service.category_id,
            sector_name: service.sector_name,
            subsector_name: service.subsector_name || "",
            name: service.name,
            caen_codes: service[`CAEN_rev_${service.caen_version || 3}`],
            lista_oficiala: service.lista_oficiala,
            lista_details: service.lista_details,
            smart_search: service.smart_search,
          }));
          setManualServices(allServices);
        }

        // Codurile autorizate vin în același răspuns (căutate pe CUI), fără al doilea apel.
        setIsLoadingCAENLookup(true);
        setCaenSecundar(
          (raspuns?.coduri_autorizate ?? [])
            .filter(
              (cod: CodCaenAutorizat) =>
                cod.cod !== entityData.caen_principal?.cod
            )
            .map((cod: CodCaenAutorizat) => cod.cod)
        );

        // Step 4: Fetch all services for remaining services (no query parameters)
        const { data: allServicesData, error: allServicesError } =
          await supabase.functions.invoke("api/get-lookup-services", {
            method: "GET",
          });

        if (allServicesError) {
          throw allServicesError;
        }

        console.log("All services fetched:", allServicesData);

        setRemainingServices(allServicesData);
      } catch (error) {
        console.error("Error fetching all services:", error);
        console.error("Error fetching data:", error);
      } finally {
        setIsLoadingAllServices(false);
        console.log("🚀 ~ fetchData ~ finally:");
        // Ensure loading screen shows for at least MIN_LOADING_TIME
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

        setTimeout(() => {
          setShowInitialLoading(false);
        }, remainingTime);
      }
    };

    fetchData();
  }, [
    entityData.cif,
    currentSessionId,
    entityData.caen_principal?.cod,
    entityData.selected_services,
    reincarcare,
  ]);

  const handleCaenPrincipalManual = async (cod: string) => {
    await actualizeazaCamp.mutateAsync({
      formSessionId: currentSessionId,
      fieldUpdates: { caen_principal: { cod, denumire: "" } },
    });
    setCaenPrincipalLipsa(false);
    setReincarcare((valoare) => valoare + 1);
  };

  // Remove the old separate useEffect for service fetching

  // New handlers for merged section
  const handleServiceToggle = (service: LookupService) => {
    const selectedService: SelectedService = {
      entity_id: service.entity_id,
      category_id: service.category_id,
      sector_name: service.sector_name,
      subsector_name: service.subsector_name || "",
      name: service.name,
    };

    if (service.category_id === "1") {
      setAnexa1Selections((prev) => {
        // TODO check if this is correct
        const isSelected = prev.some((s) => s.entity_id === service.entity_id);
        return isSelected
          ? prev.filter((s) => s.entity_id !== service.entity_id)
          : [...prev, selectedService];
      });
    } else {
      setAnexa2Selections((prev) => {
        const isSelected = prev.some((s) => s.entity_id === service.entity_id);
        return isSelected
          ? prev.filter((s) => s.entity_id !== service.entity_id)
          : [...prev, selectedService];
      });
    }

    // Synchronize with manualServices based on entity_id
    setManualServices((prev) => {
      const isSelected = prev.some((s) => s.entity_id === service.entity_id);
      if (isSelected) {
        // Remove from manualServices
        return prev.filter((s) => s.entity_id !== service.entity_id);
      } else {
        // Add to manualServices with caen_codes
        return [
          ...prev,
          {
            ...selectedService,
          },
        ];
      }
    });
  };

  const handleServiceClick = (service: IdentifiedService) => {
    setActiveService(service);
    // Clear third column selections when switching services
    setSelectedListaDetails(null);
    setSelectedSmartSearch(false);
    setHoveredCaen("");
  };

  const handleListaDetailsClick = (
    details: IdentifiedService["lista_details"][0] | null
  ) => {
    setSelectedListaDetails(details);
  };

  const handleSmartSearchClick = (selected: boolean) => {
    setSelectedSmartSearch(selected);
  };

  const handleManualServiceToggle = (service: IdentifiedService) => {
    console.log("🚀 ~ handleManualServiceToggle ~ service:", service);
    const selectedService: SelectedService = {
      entity_id: service.entity_id,
      category_id: service.category_id,
      sector_name: service.sector_name,
      subsector_name: service.subsector_name || "",
      name: service.name,
      caen_codes: service[`CAEN_rev_${service.caen_version || 3}`],
      lista_oficiala: service.lista_oficiala,
      lista_details: service.lista_details,
      smart_search: service.smart_search,
    };

    setManualServices((prev) => {
      const isSelected = prev.some((s) => s.entity_id === service.entity_id);
      return isSelected
        ? prev.filter((s) => s.entity_id !== service.entity_id)
        : [...prev, selectedService];
    });

    // Synchronize with anexa selections based on entity_id
    if (service.category_id === "1") {
      setAnexa1Selections((prev) => {
        const isSelected = prev.some((s) => s.entity_id === service.entity_id);
        return isSelected
          ? prev.filter((s) => s.entity_id !== service.entity_id)
          : [...prev, selectedService];
      });
    } else {
      setAnexa2Selections((prev) => {
        const isSelected = prev.some((s) => s.entity_id === service.entity_id);
        return isSelected
          ? prev.filter((s) => s.entity_id !== service.entity_id)
          : [...prev, selectedService];
      });
    }
  };

  // Filter remaining services based on selected sector and subsector
  const filteredRemainingServices = remainingServices.filter((service) => {
    // If no sector is selected or clear value, show all services
    if (!selectedSector || selectedSector === CLEAR_VALUE) return true;

    // Find the sector name from the selected sector ID
    const sectorName = SECTOARE[selectedSector as keyof typeof SECTOARE];
    if (!sectorName) return true;

    // First filter by sector
    const matchesSector = service.sector_name === sectorName;

    // If no subsector is selected or clear value, return services that match the sector
    if (!selectedSubsector || selectedSubsector === CLEAR_VALUE)
      return matchesSector;

    // If subsector is also selected, filter by both sector and subsector
    const subsectorName =
      SUBSECTOARE[selectedSubsector as keyof typeof SUBSECTOARE];
    if (!subsectorName) return matchesSector;

    // Check if the service's subsector matches the selected subsector name
    const matchesSubsector = service.subsector_name === subsectorName;

    return matchesSector && matchesSubsector;
  });

  // Get available subsectors for the selected sector
  const getAvailableSubsectors = () => {
    if (!selectedSector) return [];

    return Object.entries(SUBSECTOARE).filter(([key, value]) =>
      key.startsWith(selectedSector)
    );
  };

  // Reset subsector when sector changes
  const handleSectorChange = (sectorId: string) => {
    setSelectedSector(sectorId === CLEAR_VALUE ? "" : sectorId);
    setSelectedSubsector(""); // Reset subsector selection
  };

  // Handle subsector change
  const handleSubsectorChange = (subsectorId: string) => {
    setSelectedSubsector(subsectorId === CLEAR_VALUE ? "" : subsectorId);
  };

  // Extract CAEN descriptions from services for ServiceTabs
  const extractCaenDescriptions = (
    services: LookupService[]
  ): Record<string, { title: string; description: string }> => {
    const caenDescriptions: Record<
      string,
      { title: string; description: string }
    > = {};

    services.forEach((service) => {
      service[`CAEN_rev_${service.caen_version || 3}_detailed`].forEach(
        (caenData) => {
          if (!caenDescriptions[caenData.caen]) {
            // Use the service's description as the CAEN description since it comes from the lookup data
            caenDescriptions[caenData.caen] = {
              title: `Activități CAEN ${caenData.caen}`,
              description: caenData.description,
            };
          }
        }
      );
    });

    return caenDescriptions;
  };

  const buildSelectedServices = () => {
    return manualServices
      .filter(
        (service, index, self) =>
          index === self.findIndex((s) => s.entity_id === service.entity_id)
      )
      .map((service) => ({
        entity_id: service.entity_id,
        category_id: service.category_id,
        sector_name: service.sector_name,
        subsector_name: service.subsector_name,
        name: service.name,
        lista_oficiala: service.lista_oficiala,
        lista_details: service.lista_details,
        smart_search: service.smart_search,
      }));
  };

  const proceedWithNext = (services: SelectedService[]) => {
    const mockSectorData = {
      allServices: remainingServices,
      sectorsIdentified: services
        .map((service) => service.sector_name)
        .filter((sector, index, self) => self.indexOf(sector) === index),
      absolutePriority: {
        isDNSProvider: false,
        isTLDRegistry: false,
        isQualifiedTrustProvider: false,
        isCentralPublicAdmin: false,
      },
      caenValidation: {
        validatedCAENs: [
          entityData.caen_principal?.cod,
          ...entityData.caen_secundar,
        ],
        annexMapping: services.map((service) => ({
          caen: [],
          annex: service.category_id === "1" ? "Anexa 1" : "Anexa 2",
        })),
      },
    };
    onNext(
      services,
      mockSectorData,
      caenSecundar,
      relatieStatRoman,
      situatieServiciiUE
    );
  };

  const handleValidateAndContinue = () => {
    if (totalSelectedServices === 0) return;
    if (!validateSections()) return;

    const allSelectedServices = buildSelectedServices();

    if (
      haveServicesChanged(entityData.selected_services, allSelectedServices) &&
      entityData.impact_analysis
    ) {
      setShowWarningModal(true);
      return;
    }

    proceedWithNext(allSelectedServices);
  };

  // biome-ignore lint/correctness/noNestedComponentDefinitions: refactoring needed
  const ServiceCard = ({
    service,
    isSelected,
    onToggle,
    readOnly = false,
  }: {
    service: LookupService;
    isSelected: boolean;
    onToggle: (service: LookupService) => void;
    readOnly?: boolean;
  }) => {
    return (
      <Card
        key={service.id}
        className={`${isSelected ? "border-primary bg-primary/5" : ""} ${readOnly ? "opacity-75" : ""} ${!readOnly ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}`}
        onClick={!readOnly ? () => onToggle(service) : undefined}
      >
        <CardContent className="p-3 md:p-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant={
                    service.category_id === "1" ? "destructive" : "secondary"
                  }
                  className="text-xs whitespace-nowrap"
                >
                  {service.category_id === "1" ? "Anexa 1" : "Anexa 2"}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedServiceInfo(service);
                    setServiceInfoModalOpen(true);
                  }}
                >
                  <Info className="h-4 w-4 text-primary" />
                </Button>
                <div className="ml-auto">
                  {!readOnly ? (
                    <Checkbox
                      checked={isSelected}
                      className="pointer-events-none"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-success" />
                      <span className="text-xs text-success font-medium">
                        Auto-selectat
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <h4 className="font-semibold text-primary text-sm">
                {service.name}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {service.subsector_name}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const totalSelectedServices = manualServices.length;

  const servicesAnexa1 = identifiedServices.filter(
    (s) => s.category_id === "1"
  );
  const servicesAnexa2 = identifiedServices.filter(
    (s) => s.category_id === "2"
  );

  // Show loading screen on initial load
  if (
    showInitialLoading &&
    (isLoadingIdentifiedServices || isLoadingCAENLookup)
  ) {
    //   if (
    //     showInitialLoading &&
    //     (isLoadingIdentifiedServices || isLoadingCAENLookup)
    //   ) {
    return (
      <WizardLayout
        title="Identificare și Mapare Servicii NIS 2"
        currentStep={currentStep}
        steps={steps || STEPS}
        onBack={onBack}
        onStepClick={onStepClick}
        userAvatar={userAvatar}
        fullWidth
      >
        <LoadingServiceIdentification
          onComplete={() => setShowInitialLoading(false)}
        />
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      title="Identificare Servicii NIS 2"
      description="Selectați serviciile care se încadrează în domeniul de aplicare al legislației NIS 2 pentru entitatea dvs."
      currentStep={currentStep}
      steps={steps}
      currentSubSteps={[
        {
          id: "2.A",
          label: "Servicii identificate automat",
          status: "completed",
        },
        {
          id: "2.B",
          label: "Servicii autorizate & manual",
          status: "current",
        },
      ]}
      onBack={onBack}
      onStepClick={onStepClick}
      showBackButton
      fullWidth
      userAvatar={userAvatar}
      footer={
        <>
          <div className="text-sm text-ds-text-500 hidden md:flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span>Total servicii: {totalSelectedServices}</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Button variant="outline" onClick={onBack}>
              Înapoi
            </Button>
            <PaywallButton
              locked={false}
              rightIcon={<ArrowRight className="w-4 h-4" />}
              variant="hero"
              onClick={handleValidateAndContinue}
              disabled={
                totalSelectedServices === 0 ||
                isLoadingAllServices ||
                isLoadingIdentifiedServices
              }
              className="min-w-[200px]"
            >
              Continuă evaluarea
            </PaywallButton>
          </div>
        </>
      }
    >
      <div className="space-y-4 md:space-y-6">
        {/* New Merged Section: Servicii identificate automat din codurile CAEN */}
        <Card>
          <CardHeader className="p-3 md:p-4">
            <div className="flex items-center gap-3">
              <Network className="hidden md:block w-5 h-5 text-primary" />
              <div>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <Network className="block md:hidden w-3 h-3 text-primary" />
                  Identificare avansată a serviciilor NIS2
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  NIS2PASS verifică automat compania ta în listele oficiale ale
                  autorităților (ANRE, ANCOM etc.) și folosind motorul AI
                  SmartSearch identifică serviciile relevante și generează un
                  scor de aplicabilitate pentru o evaluare precisă.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 md:p-4 space-y-4">
            {caenPrincipalLipsa && (
              <CaenPrincipalSelector
                coduri={coduriAutorizate}
                onConfirm={handleCaenPrincipalManual}
                isSaving={actualizeazaCamp.isPending}
              />
            )}
            {isLoadingAllServices || isLoadingIdentifiedServices ? (
              <div className="space-y-4">
                <div className="flex space-x-1">
                  <Skeleton className="h-10 w-24" />
                  <Skeleton className="h-10 w-24" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Skeleton className="h-64" />
                  <Skeleton className="h-64" />
                  <Skeleton className="h-64" />
                </div>
              </div>
            ) : identifiedServices.length > 0 ? (
              <Tabs
                value={selectedAnexa}
                onValueChange={(value) => {
                  setSelectedAnexa(value as "1" | "2");
                  // Clear third column selections when switching tabs
                  setSelectedListaDetails(null);
                  setSelectedSmartSearch(false);
                  setHoveredCaen("");
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <TabsList className="bg-ds-surface-100 p-1">
                    <TabsTrigger
                      value="1"
                      className="text-xs md:text-sm flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-ds-border-200 transition-all duration-200"
                    >
                      Anexa 1
                      <Badge
                        variant={
                          selectedAnexa === "1" ? "secondary" : "outline"
                        }
                        className={`text-xs ${
                          selectedAnexa === "1"
                            ? "bg-primary-foreground text-primary"
                            : "bg-background text-foreground"
                        }`}
                      >
                        {servicesAnexa1.length}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger
                      value="2"
                      className="text-xs md:text-sm flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-ds-border-200 transition-all duration-200"
                    >
                      Anexa 2
                      <Badge
                        variant={
                          selectedAnexa === "2" ? "secondary" : "outline"
                        }
                        className={`text-xs ${
                          selectedAnexa === "2"
                            ? "bg-primary-foreground text-primary"
                            : "bg-background text-foreground"
                        }`}
                      >
                        {servicesAnexa2.length}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs md:text-sm">
                      {selectedAnexa === "1"
                        ? anexa1Selections.length
                        : anexa2Selections.length}{" "}
                      /{" "}
                      {selectedAnexa === "1"
                        ? servicesAnexa1.length
                        : servicesAnexa2.length}{" "}
                      <span className="hidden md:inline ml-1">selectate</span>
                    </Badge>
                  </div>
                </div>

                <TabsContent value="1" className="mt-0">
                  <ServiceTabs
                    services={servicesAnexa1}
                    selections={anexa1Selections}
                    activeService={activeService}
                    hoveredCaen={hoveredCaen}
                    selectedListaDetails={selectedListaDetails}
                    selectedSmartSearch={selectedSmartSearch}
                    caenDescriptions={extractCaenDescriptions(
                      identifiedServices
                    )}
                    onServiceToggle={handleServiceToggle}
                    onServiceClick={handleServiceClick}
                    onCaenHover={setHoveredCaen}
                    onListaDetailsClick={handleListaDetailsClick}
                    onSmartSearchClick={handleSmartSearchClick}
                  />
                </TabsContent>

                <TabsContent value="2" className="mt-0">
                  <ServiceTabs
                    services={servicesAnexa2}
                    selections={anexa2Selections}
                    activeService={activeService}
                    hoveredCaen={hoveredCaen}
                    selectedListaDetails={selectedListaDetails}
                    selectedSmartSearch={selectedSmartSearch}
                    caenDescriptions={extractCaenDescriptions(
                      identifiedServices
                    )}
                    onServiceToggle={handleServiceToggle}
                    onServiceClick={handleServiceClick}
                    onCaenHover={setHoveredCaen}
                    onListaDetailsClick={handleListaDetailsClick}
                    onSmartSearchClick={handleSmartSearchClick}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Network className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>
                  Nu s-au identificat servicii NIS 2 pentru codurile CAEN ale
                  entității.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Section 3: Din cautare manuala */}
        <Collapsible open={manualSearchOpen} onOpenChange={setManualSearchOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Search className="hidden md:block w-5 h-5 text-ds-orange-500" />
                    <div>
                      <CardTitle className="text-base md:text-lg flex items-center gap-2">
                        <Search className="block md:hidden w-3 h-3 text-ds-orange-500" />
                        Din cautare manuala
                      </CardTitle>
                      <CardDescription className="text-xs md:text-sm">
                        Servicii suplimentare care pot fi relevante pentru
                        entitate
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="whitespace-nowrap">
                      {
                        manualServices.filter(
                          (service, index, self) =>
                            index ===
                            self.findIndex(
                              (s) => s.entity_id === service.entity_id
                            )
                        ).length
                      }
                      /{remainingServices.length}{" "}
                      <span className="hidden md:inline ml-1">selectate</span>
                    </Badge>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${manualSearchOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {remainingServices.length > 0 ? (
                  <div className="space-y-4">
                    {manualServices.length === 0 && (
                      <div className="flex items-center gap-2 p-3 bg-ds-orange-400/10 border border-ds-orange-400/20 rounded-lg">
                        <p className="text-sm text-ds-orange-500">
                          Selectați manual orice servicii suplimentare care ar
                          putea fi relevante pentru activitatea entității.
                        </p>
                      </div>
                    )}

                    {/* Sector and Subsector Dropdowns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label
                          htmlFor="sector-select"
                          className="text-sm font-medium"
                        >
                          Sector
                        </label>
                        <Select
                          value={selectedSector}
                          onValueChange={handleSectorChange}
                        >
                          <SelectTrigger
                            id="sector-select"
                            className={
                              !selectedSector ? "text-muted-foreground" : ""
                            }
                          >
                            <SelectValue placeholder="Selectați sectorul" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={CLEAR_VALUE}>
                              <span className="text-muted-foreground">
                                Șterge selecția
                              </span>
                            </SelectItem>
                            {Object.entries(SECTOARE).map(([id, name]) => (
                              <SelectItem
                                key={id}
                                value={id}
                                textValue={String(name)}
                              >
                                {String(name)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="subsector-select"
                          className="text-sm font-medium"
                        >
                          Subsector
                        </label>
                        <Select
                          value={selectedSubsector}
                          onValueChange={handleSubsectorChange}
                          disabled={!selectedSector}
                        >
                          <SelectTrigger
                            id="subsector-select"
                            className={
                              !selectedSubsector ? "text-muted-foreground" : ""
                            }
                          >
                            <SelectValue placeholder="Selectați subsectorul" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={CLEAR_VALUE}>
                              <span className="text-muted-foreground">
                                Șterge selecția
                              </span>
                            </SelectItem>
                            {getAvailableSubsectors().map(([id, name]) => (
                              <SelectItem
                                key={id}
                                value={id}
                                textValue={String(name)}
                              >
                                {String(name)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {(selectedSector && selectedSector !== CLEAR_VALUE) ||
                    (selectedSubsector && selectedSubsector !== CLEAR_VALUE) ? (
                      filteredRemainingServices.length > 0 ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">
                              Servicii disponibile (
                              {filteredRemainingServices.length})
                            </h4>
                          </div>
                          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
                            {filteredRemainingServices.map((service) => (
                              <ServiceCard
                                key={service.id}
                                service={service}
                                isSelected={manualServices.some(
                                  (s) => s.entity_id === service.entity_id
                                )}
                                onToggle={handleManualServiceToggle}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 text-muted-foreground">
                          <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />
                          <p>
                            {selectedSubsector &&
                            selectedSubsector !== CLEAR_VALUE
                              ? "Nu s-au găsit servicii pentru subsectorul selectat"
                              : "Nu s-au găsit servicii pentru sectorul selectat"}
                          </p>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />
                        <p>
                          Selectați un sector pentru a vedea serviciile
                          disponibile
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>
                      Toate serviciile disponibile au fost deja clasificate în
                      secțiunile anterioare.
                    </p>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Selected Services Summary */}
        <SelectedServicesSummary
          selectedServices={manualServices}
          collapsible
          onRemove={(service) => {
            const entityId = service.entity_id;
            setManualServices((prev) =>
              prev.filter((s) => s.entity_id !== entityId)
            );
            setAnexa1Selections((prev) =>
              prev.filter((s) => s.entity_id !== entityId)
            );
            setAnexa2Selections((prev) =>
              prev.filter((s) => s.entity_id !== entityId)
            );
          }}
        />

        {totalSelectedServices === 0 && (
          <div className="flex items-center gap-2 p-3 bg-ds-amber-100 border border-ds-amber-500/20 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-ds-amber-500" />
            <p className="text-sm text-navy-800">
              Vă rugăm să selectați cel puțin un serviciu pentru a continua
              evaluarea.
            </p>
          </div>
        )}

        {/* Relația cu Statul Român + Servicii în UE */}
        <Step2AdditionalSections
          entityDataForSections={entityDataForSections}
          handlers={handlers}
          sectionErrors={sectionErrors}
        />
      </div>

      {/* Service Info Modal */}
      <ServiceInfoModal
        open={serviceInfoModalOpen}
        onOpenChange={setServiceInfoModalOpen}
        service={selectedServiceInfo}
      />

      {/* Warning: Services changed with existing impact analysis */}
      <ServiceChangesWarningModal
        open={showWarningModal}
        onOpenChange={setShowWarningModal}
        onConfirm={() => {
          setShowWarningModal(false);
          proceedWithNext(buildSelectedServices());
        }}
      />
    </WizardLayout>
  );
}
